import { isEmptyObject, isTelematics } from '@acorn-portal/shared/src/utils';
import { Box, Collapse, Typography } from '@mui/material';
import BackButton from '@acorn-portal/shared/src/components/buttons/BackButton';
import ContinueButton from '@acorn-portal/shared/src/components/buttons/ContinueButton';
import VehicleLookup from '@acorn-portal/shared/src/components/vehicleLookup';
import { Form, Formik, FormikHelpers } from 'formik';
import { useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import useTrack from '@acorn-portal/shared/src/hooks/useTrack/useTrack';
import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { MtaChangeType } from '@acorn-portal/shared/src/services/api';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import useGetOrCreateMidTermAdjustment from '@acorn-portal/shared/src/models/midTermAdjustment-model';
import { useDiscardMta } from '@acorn-portal/shared/src/hooks/use-discard-mta';
import JourneyCardLayout from '@acorn-portal/shared/src/layouts/journey-card-layout';
import HelperModal from '@acorn-portal/shared/src/components/modal/HelperModal';
import { Product, TelematicDeviceProvider } from '@acorn-portal/shared/src/services/interface/Shared';
import ActionButtons from '@acorn-portal/shared/src/components/wrapper/action-buttons';
import VehicleChangeWarning from '@acorn-portal/shared/src/components/copy/VehicleChangeWarning';
import ToggleButtonFormField from '@acorn-portal/shared/src/components/inputs/toggle-button-form-field';
import { IOptions } from '@acorn-portal/shared/src/components/inputs/select';
import ChangeOfVehiclePart1Schema from '@acorn-portal/shared/src/utils/validation/ChangeOfVehiclePart1Schema';
import { VEHICLE_DETAILS_PART_ONE_ERROR_MESSAGES } from '@acorn-portal/shared/src/utils/constants/ErrorMessages';
import { FormikLogErrors } from '@acorn-portal/shared/src/pages/mta/components/FormikLogErrors';

interface IChangeOfVehiclePart1 {
	vrm: string;
	bodyType?: string;
}

const ChangeOfVehiclePart1: FC = () => {
	const [open, toggleModal] = useState(false);
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();

	const { midTermAdjustment } = useGetOrCreateMidTermAdjustment({
		changeType: MtaChangeType.CHANGE_VEHICLE,
		setAdjustmentLoading: setLoading,
	});

	const { discardLoading } = useDiscardMta();

	const policy = useAppSelector((redux) => redux.fullPolicy);
	const vehicle = useAppSelector((redux) => redux.vehicleLookup);
	const lists = useAppSelector((redux) => redux.referenceData);

	const isTaxi = policy.product === Product.TAXI;
	const isTelematicPolicy = isTelematics(policy.product);
	const { track } = useTrack();

	useEffect(() => {
		track(`Vehicle Details 1 Page Viewed`);
	}, []);

	const initialValues: IChangeOfVehiclePart1 = {
		vrm: '',
		bodyType: '',
	};

	const handleOnSubmit = (values: IChangeOfVehiclePart1, formikHelpers: FormikHelpers<IChangeOfVehiclePart1>) => {
		if (isEmptyObject(vehicle.vehicleInfo)) {
			formikHelpers.setErrors({
				vrm: VEHICLE_DETAILS_PART_ONE_ERROR_MESSAGES.emptyVrm,
			});
			return;
		}
		if (
			vehicle.vehicleInfo.registrationNumber ===
			midTermAdjustment.vehicleDetails.registration.replace(/\s/g, '').toUpperCase()
		) {
			formikHelpers.setErrors({
				vrm: VEHICLE_DETAILS_PART_ONE_ERROR_MESSAGES.sameVrm,
			});
			return;
		}

		if (
			(vehicle.isCommercialVehicle && policy.product !== (Product.COMMERCIAL_VEHICLE || Product.TAXI)) ||
			(!vehicle.isCommercialVehicle && policy.product === Product.COMMERCIAL_VEHICLE)
		) {
			formikHelpers.setErrors({
				vrm: VEHICLE_DETAILS_PART_ONE_ERROR_MESSAGES.vehicleTypeNotInsurable,
			});
			return;
		}

		if (vehicle.isMotorCycle) {
			formikHelpers.setErrors({
				vrm: VEHICLE_DETAILS_PART_ONE_ERROR_MESSAGES.motorCyclesNotInsurable,
			});
			return;
		}
		navigate(ROUTE_PATHS.makeAChangeVehicle2, {
			state: {
				registration: values.vrm,
				yearOfManufacture: vehicle.vehicleInfo.year ?? 0,
				bodyType: values.bodyType ?? vehicle.vehicleInfo.bodyType?.code,
			},
		});
	};

	const taxiBodyOptions = lists.bodyTypeTypes.filter(
		(obj) =>
			obj.value === '01' ||
			obj.value === '04' ||
			obj.value === '02' ||
			obj.value === '03' ||
			obj.value === '62' ||
			obj.value === '07' ||
			obj.value === 'C8' ||
			obj.value === '3TCHDHA7',
	);

	return (
		<JourneyCardLayout pageTitle='New Vehicle Details' loading={loading || discardLoading}>
			<Typography
				sx={{
					margin: 'auto',
					paddingBottom: '1rem',
					textAlign: 'center',
				}}
			>
				Please enter a new vehicle registration below.
			</Typography>
			<Formik
				initialValues={initialValues}
				validationSchema={ChangeOfVehiclePart1Schema({
					fullPolicy: policy,
				})}
				onSubmit={handleOnSubmit}
			>
				{(formik) => (
					<Form>
						<FormikLogErrors />
						<VehicleLookup />

						{isTelematicPolicy && (
							<Box maxWidth='md' margin='auto'>
								<VehicleChangeWarning device={policy.coverDetails.deviceProvider as TelematicDeviceProvider} />
							</Box>
						)}
						{isTaxi && (
							<Collapse in={typeof vehicle?.vehicleInfo?.bodyType?.desc === 'string'}>
								<ToggleButtonFormField
									label='What is your vehicle body type?'
									name='bodyType'
									id='bodyType'
									value={formik.values.bodyType}
									onChange={(value) => formik.setFieldValue('bodyType', value)}
									options={taxiBodyOptions as IOptions[]}
									error={formik.touched.bodyType && Boolean(formik.errors.bodyType)}
									helperText={formik.touched.bodyType && formik.errors.bodyType}
								/>
							</Collapse>
						)}

						<ActionButtons>
							<BackButton onClick={() => toggleModal(true)}>Back</BackButton>
							<ContinueButton type='submit'>Continue</ContinueButton>
						</ActionButtons>
					</Form>
				)}
			</Formik>
			<HelperModal
				open={open}
				onClose={() => {
					toggleModal(false);
				}}
				title='Discard The Changes?'
				description='Are you sure you wish to discard the changes?'
				closeText='No'
				submitText='Yes'
				onSubmit={() => navigate(-1)}
			/>
		</JourneyCardLayout>
	);
};

export default ChangeOfVehiclePart1;
